import { type LineItem } from '@commercetools/platform-sdk'
import { useLoaderData, useSearchParams } from '@remix-run/react'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import PlentyBody from '#app/components/typography/plenty-body'
import PlentyHeading from '#app/components/typography/plenty-heading'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import {
	Sheet,
	SheetContent,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '#app/components/ui/sheet'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { useFetcherWithReset } from '#app/hooks/useFetcherWithReset'
import {
	type loader,
	type action,
} from '#app/routes/_public+/product+/$styleNumber'
import { useCartStore } from '#app/store/cart'
import { type PlentyVariantAvailability } from '#types/product'
import GoogleMapComponent from './google-maps'
import RadioGroupStores from './radio-group-stores'
import SearchStoreInput from './search-store-input'

interface ChooseStoreProps {
	firstAvailability: PlentyVariantAvailability[]
	triggerLabel: string
	sheetTitle: string
	triggerClassName?: string
	triggerOnClick?: (e: React.MouseEvent) => void
	sheetTriggerRef: React.RefObject<HTMLButtonElement>
	selectedSku: string | null | undefined
}

const ChooseStore: React.FC<ChooseStoreProps> = ({
	firstAvailability,
	triggerLabel,
	sheetTitle,
	triggerClassName,
	triggerOnClick,
	sheetTriggerRef,
	selectedSku,
}) => {
	const { t } = useTranslation('shopping_bag')
	const [isOpen, setIsOpen] = useState(false)
	const [isTriggerClicked, setIsTriggerClicked] = useState(false)
	const [selectedChannelId, setSelectedChannelId] = useState('')
	const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({
		lat: firstAvailability[0]?.geoLocation?.coordinates[1] as number,
		lng: firstAvailability[0]?.geoLocation?.coordinates[0] as number,
	})
	const [zoom, setZoom] = useState(13)
	const centerLng = firstAvailability[0]?.geoLocation?.coordinates[1] as number
	const centerLat = firstAvailability[0]?.geoLocation?.coordinates[0] as number
	const { trackAmplitudeEvent } = useAmplitudeContext()
	const loaderData = useLoaderData<typeof loader>()
	const productData = loaderData?.productData
	const activeStyle =
		productData?.styleAvailableVariants.find(item => item.active) ||
		productData!.styleAvailableVariants[0]
	const { cart } = useCartStore()
	const [searchParams] = useSearchParams()
	const productFetcher = useFetcherWithReset<typeof action>({ key: 'addToBag' })
	const handleSheetTriggerClick = (e: React.MouseEvent) => {
		if (!selectedSku) {
			e.preventDefault()
			if (triggerOnClick) {
				triggerOnClick(e)
			}
			setIsTriggerClicked(true)
		} else {
			trackAmplitudeEvent('open click and collect sheet', {
				selectedSku: selectedSku,
				productName: productData?.productInfo.name,
				activeStyle: activeStyle.color,
			})
			setIsOpen(true)
		}
	}

	useEffect(() => {
		if (isTriggerClicked && selectedSku) {
			setIsOpen(true)
			setIsTriggerClicked(false)
		}
	}, [selectedSku, isTriggerClicked])

	const handleSelectStoreClick = () => {
		setIsOpen(false)
	}

	const getQuantity = (storeKey?: string) => {
		const selectedVariant = activeStyle.variants.find(
			variant => variant.sku === selectedSku,
		)
		if (!selectedVariant || !cart) {
			return 0
		}

		const channelId = selectedVariant.availability?.find(
			it => it.storeKey === storeKey,
		)?.channelKey
		return cart.lineItems.reduce((acc: number, item: LineItem) => {
			const isCorrectVariant = item.variant.sku === selectedVariant.sku
			const isCorrectChannel = storeKey
				? item.distributionChannel?.id === channelId
				: true
			if (isCorrectVariant && isCorrectChannel) {
				return acc + item.quantity
			}
			return acc
		}, 0)
	}

	const getVariantAvailability = (storeKey?: string) => {
		const selectedVariant = activeStyle.variants.find(
			variant => variant.sku === selectedSku,
		)
		if (!selectedVariant) {
			return 0
		}
		if (storeKey) {
			const availability = selectedVariant.availability?.find(
				availability => availability.storeKey === storeKey,
			)
			return availability?.availableQuantity || 0
		}
		return (
			selectedVariant.availability?.reduce((total, availability) => {
				const quantity = (availability.availableQuantity as number) || 0
				return total + quantity
			}, 0) || 0
		)
	}

	const handleAddToBag = () => {
		if (selectedSku) {
			const selectedAvailability = activeStyle.variants
				.find(variant => variant.sku === selectedSku)
				?.availability?.find(
					it =>
						(it.availableQuantity as number) > 0 &&
						loaderData?.storeParams.includes(it.storeKey as string),
				)

			const cartQuantity = getQuantity(selectedAvailability?.storeKey)
			const availableQuantity = getVariantAvailability(
				selectedAvailability?.storeKey,
			)
			if (cartQuantity >= availableQuantity) {
				toast(
					t('bag_quantity_reached_error', {
						cartQuantity: cartQuantity,
						availableQuantity: availableQuantity,
					}),
					{ icon: <Icon name="exclamation" size="lg" /> },
				)
				return
			}
			trackAmplitudeEvent('product added click and collect pdp', {
				sku: selectedSku,
				styleNumber: productData?.productInfo.key || '',
				storeKey: loaderData?.storeParams.length
					? (selectedAvailability?.storeKey as string)
					: '',
				channelId: selectedChannelId || '',
			})
			productFetcher.submit(
				{
					sku: selectedSku,
					styleNumber: productData?.productInfo.key || '',
					storeKey: loaderData?.storeParams.length
						? (selectedAvailability?.storeKey as string)
						: '',
					channelId: selectedChannelId || '',
				},
				{
					method: 'POST',
					action: `/product/${productData?.productInfo
						.key}?${searchParams.toString()}`,
				},
			)
			setIsOpen(false)
		} else {
			handleSelectStoreClick()
		}
	}
	const locations = firstAvailability
		.filter(store => store.geoLocation && store.geoLocation.coordinates)
		.map(store => ({
			lat: store.geoLocation!.coordinates[1], // Ensure correct coordinates (lat, lng)
			lng: store.geoLocation!.coordinates[0],
			storeName: store.storeName as string,
			storeId: store.storeKey as string,
			storeChannelKey: store.channelKey as string,
		}))

	const handleStoreClick = (storeChannelKey: string) => {
		if (selectedChannelId !== storeChannelKey) {
			setSelectedChannelId(storeChannelKey)
		}
	}

	const handleSearchLocation = async (location: string) => {
		const geocoder = new google.maps.Geocoder()
		geocoder.geocode({ address: location }, (results, status) => {
			if (status === 'OK' && results && results.length > 0) {
				setZoom(10) // Set the zoom level to 5 on search
				const { lat, lng } = results[0].geometry.location
				setMapCenter({ lat: lat(), lng: lng() })
			} else {
				console.error(
					'Geocode was not successful for the following reason: ' + status,
				)
			}
		})
	}
	return (
		<Sheet open={isOpen} onOpenChange={setIsOpen}>
			<SheetTrigger
				className={triggerClassName}
				onClick={handleSheetTriggerClick}
				ref={sheetTriggerRef}
			>
				{triggerLabel}
			</SheetTrigger>
			<SheetContent className="flex h-full flex-col p-0">
				<SheetHeader className="mb-4 px-6 pt-6">
					<SheetTitle asChild>
						<PlentyHeading className="mr-6 lg:mr-0" as={'h7'}>
							{sheetTitle}
						</PlentyHeading>
					</SheetTitle>
				</SheetHeader>
				<div className=" flex flex-1 flex-col gap-4 overflow-y-auto">
					<div className="hidden md:flex">
						<SearchStoreInput onSearch={handleSearchLocation} />
					</div>
					<div className="hidden px-4 md:flex md:px-6">
						{centerLat && centerLng ? (
							<GoogleMapComponent
								locations={locations}
								zoom={zoom}
								selectedStoreChannelKey={selectedChannelId}
								onMarkerClick={handleStoreClick} // Pass handler to GoogleMapComponent
								center={mapCenter}
							/>
						) : (
							<div>Loading Map...</div>
						)}
					</div>
					<PlentyBody size="md" className="px-4 md:px-6">
						{t('product_details_page:stores_found', {
							count: firstAvailability?.length,
						})}
					</PlentyBody>
					<RadioGroupStores
						stores={firstAvailability}
						name="newPickupStore"
						onSelectChannel={handleStoreClick}
						selectedChannelKey={selectedChannelId}
						mapCenter={mapCenter}
					/>
				</div>
				<SheetFooter className="px-8 pb-8 pt-6 md:pb-6 md:pt-6">
					<Button
						className="text-md w-full px-8 py-4"
						name="intent"
						value="convertToPickup"
						onClick={handleAddToBag}
					>
						{t('select_this_store')}
					</Button>
				</SheetFooter>
			</SheetContent>
		</Sheet>
	)
}

export default ChooseStore
